/**
 * Shared definition of the "synthetic CustomEvent" exception-noise class.
 *
 * When a Promise is rejected with a DOM event object (e.g. a `CustomEvent`)
 * instead of an `Error`, the browser's global `unhandledrejection` handler —
 * in both Sentry and posthog-js — cannot extract a message or a stack trace,
 * so it synthesizes a placeholder exception with `mechanism.synthetic === true`
 * and a value like `CustomEvent captured as exception with keys: isTrusted`.
 * These carry no stack trace and are not application faults; every occurrence
 * is untraceable noise that opens a fresh error-tracking issue for nothing.
 *
 * Sentry drops them in `makeClientBeforeSend`; PostHog drops them in its
 * `before_send` (see `instrumentation-client.ts`). Both share this one
 * predicate so the two error-tracking paths never drift on what counts as
 * noise. Kept as a pure function so it can be unit-tested without a browser.
 */

/**
 * Minimal structural shape common to a Sentry `exception.values[]` entry and a
 * PostHog `$exception_list[]` entry — both expose the exception `type` (the DOM
 * constructor name for a coerced event, e.g. `'CustomEvent'`) and a
 * `mechanism.synthetic` flag.
 */
export type SyntheticExceptionEntry = {
  type?: string;
  mechanism?: { synthetic?: boolean };
};

/**
 * True when a single exception entry is a synthetic, non-`Error`
 * `CustomEvent` — the stackless placeholder both SDKs emit for a Promise
 * rejected with a DOM event object.
 */
export function isSyntheticCustomEventException(
  entry: SyntheticExceptionEntry,
): boolean {
  return entry.type === 'CustomEvent' && entry.mechanism?.synthetic === true;
}

/**
 * True when `entries` is non-empty and *every* entry is a synthetic
 * `CustomEvent` rejection — i.e. the whole event is this noise class and
 * nothing traceable is lost by dropping it. An empty or missing list returns
 * false, so events with no exception values are never dropped by this guard.
 */
export function isAllSyntheticCustomEventExceptions(
  entries: readonly SyntheticExceptionEntry[] | undefined | null,
): boolean {
  return (
    !!entries &&
    entries.length > 0 &&
    entries.every(isSyntheticCustomEventException)
  );
}
